import { GridRowSelectionModel } from '@mui/x-data-grid-pro'
import {
  Checkbox,
  getFontCss,
  Processing,
  styleConstants,
  themeConstants,
} from '@paywithextend/components'
import _ from 'lodash'
import React, { useCallback, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import BillPayTable from 'src/components/BillPayTable/BillPayTable'
import CreditCardDropdown, {
  CreditCardDropdownInfiniteScrollProps,
  creditCardOption,
} from 'src/components/CreditCardDropdown/CreditCardDropdown'
import DividerLineDiv from 'src/components/DividerLineDiv/DividerLineDiv'
import FilterSection from 'src/components/FilterSection/FilterSection'
import KeywordFilter from 'src/components/KeywordFilter/KeywordFilter'
import NavAwayBox, { NavAwayProps } from 'src/components/NavAwayBox/NavAwayBox'
import RadioButton from 'src/components/RadioButton/RadioButton'
import { TableEmptyStateDesigns } from 'src/components/TableEmptyState/TableEmptyState'
import UserDropdown, {
  UserDropdownInfiniteScrollProps,
  userIdOption,
  UserOption,
} from 'src/components/UserDropdown/UserDropdown'
import { VirtualCardsFiltersBody } from 'src/components/VirtualCardsBoxAndLinks/VirtualCardsBoxAndLinks'
import {
  VirtualCardDeactivatingInIncrements,
  VirtualCardFilterStatus,
} from 'src/enums'
import { useBackgroundDispatch } from 'src/hooks'
import Trns, {
  translateSingleString,
  useTranslate,
} from 'src/localization/Trns'
import { VirtualCardListModel } from 'src/models/virtualCard.models'
import { VirtualCardPermissions } from 'src/models/virtualCardPermissions.models'
import { useQueryCreditCards } from 'src/queries/creditCard.queries'
import { useQueryOwnUser, useQueryUsersSearch } from 'src/queries/users.queries'
import { createGetVirtualCardsInitAction } from 'src/redux/actions/data/virtualCards.actions'
import { selectIsFiltersFlyoutOpenFromState } from 'src/redux/selectors/app.selectors'
import { selectNullSafeLocaleFromState } from 'src/redux/selectors/auth.selectors'
import { UsersFullPaginatedDataState } from 'src/redux/usersFullPaginatedData.reducer'
import { InfiniteScrollProps } from 'src/types'
import { css } from 'styled-components'

export interface BillPayBoxProps {
  defaultTitle: React.ReactElement
  virtualCards: VirtualCardListModel[]
  virtualCardsPermissions?: Array<{
    permissions: VirtualCardPermissions[]
    virtualCardId: string
  }>
  emptyStateDesign: TableEmptyStateDesigns
  isYourCards?: boolean
  loading: boolean
  complete: boolean
  totalCount: number
  fullProps?: {
    filteredCount?: number
    keywordFilter: string
    setKeywordFilter: (value: string) => void
    statusesFilter: string[]
    setStatusesFilter: (value: Set<string>) => void
    handleStatusSelection: (value: string) => void
    setPushNewSearchState?: (value: boolean) => void
    deactivatingInFilter?: string
    setDeactivatingInFilter?: (value: string) => void
    cardholdersFilter?: string[]
    setCardholdersFilter?: (value: Set<string>) => void
    handleCardholderSelection?: (value: string) => void
    creditCardsFilter?: string[]
    setCreditCardsFilter?: (value: Set<string>) => void
    handleCreditCardSelection?: (value: string) => void
    sortableProps: {
      setSort: (field: string) => void
      sortBy?: string
      sortDescending?: boolean
    }
  }
  hideFilters?: boolean
  snapshotProps?: {
    navAway: NavAwayProps
  }
  isBudget?: boolean
  readOnly?: boolean
  isSnapshot?: boolean
  usersFullPaginatedData: UsersFullPaginatedDataState
  checkboxProps?: CheckboxProps
  infiniteScrollProps?: InfiniteScrollProps<VirtualCardsFiltersBody>
  filtersBody?: VirtualCardsFiltersBody
  clientHeight?: number
}

// TODO: Update when we add bulk actions for BillPay
export interface CheckboxProps {
  selectedVirtualCards: VirtualCardListModel[]
  handleVirtualCardCheckboxClick: (option: VirtualCardListModel) => void
  handleBulkVirtualCardCheckboxClick: () => void
  handleDataGridVirtualCardCheckboxClick: (
    selection: GridRowSelectionModel,
  ) => void
  total: number
  canSelectVirtualCard: (virtalCard: VirtualCardListModel) => boolean
}

const BillPayBox: React.FC<BillPayBoxProps> = (props): React.ReactElement => {
  const dispatch = useBackgroundDispatch()
  const translate = useTranslate()
  const locale = useSelector(selectNullSafeLocaleFromState)
  const filtersFlyoutOpen = useSelector(selectIsFiltersFlyoutOpenFromState)
  const { data: user } = useQueryOwnUser({})

  const [creditCardSearch, setCreditCardSearch] = useState<string | undefined>()
  const {
    data: creditCardsData,
    fetchNextPage: fetchMoreCreditCards,
    isFetching: creditCardsLoading,
    complete: creditCardsComplete,
  } = useQueryCreditCards({
    filters: {
      search: creditCardSearch,
      count: 100,
      sortField: 'type',
      sortDirection: 'ASC',
      billPayEnabled: true,
    },
    enabled: filtersFlyoutOpen,
  })

  const [cardCreatorSearch, setCardCreatorSearch] = useState<
    string | undefined
  >()
  const {
    data: cardCreatorUsersData,
    fetchNextPage: fetchMoreCardCreatorUsers,
    isFetching: cardCreatorUsersLoading,
    complete: cardCreatorUsersComplete,
  } = useQueryUsersSearch({
    filters: {
      q: cardCreatorSearch,
      inOrganization: true,
      count: 10,
    },
    enabled: filtersFlyoutOpen,
  })

  const creditCardInfiniteScrollProps: CreditCardDropdownInfiniteScrollProps = {
    fetch: (filterInput, page) => {
      // TODO: this used to be handled implicitly by passing filterInput to the redux action
      // is there a cleaner way to handle this with react query?
      if (page === 0) setCreditCardSearch(filterInput)
      fetchMoreCreditCards()
    },
    page: creditCardsData?.page,
    loading: creditCardsLoading,
    complete: creditCardsComplete,
  }

  const creditCardOptions = useMemo(
    () =>
      creditCardsData?.creditCards?.map((creditCard) =>
        creditCardOption(creditCard),
      ) ?? [],
    [creditCardsData?.creditCards],
  )

  const cardCreatorInfiniteScrollProps: UserDropdownInfiniteScrollProps = {
    fetch: (filterInput, page) => {
      // TODO: this used to be handled implicitly by passing filterInput to the redux action
      // is there a cleaner way to handle this with react query?
      if (page === 0) setCardCreatorSearch(filterInput)
      fetchMoreCardCreatorUsers()
    },
    page: cardCreatorUsersData?.page,
    loading: cardCreatorUsersLoading,
    complete: cardCreatorUsersComplete,
  }

  const cardCreatorOptions: UserOption[] = useMemo(() => {
    return [
      // the users search endpoint has never included the current user
      // this implementation detail is buried in the getUsers action
      user ? userIdOption(user) : undefined,
      ...(cardCreatorUsersData?.users?.map((user) => userIdOption(user)) ?? []),
    ].filter((option): option is UserOption => !!option)
  }, [cardCreatorUsersData?.users, user])

  const pushUrlStateDebounce = useCallback(
    _.debounce(() => {
      props.fullProps?.setPushNewSearchState?.(true)
    }, 500),
    [],
  )

  const onKeywordChange = (v: string): void => {
    props.fullProps?.setKeywordFilter?.(v)
  }

  const resetKeywordFilter = (): void => {
    props.fullProps?.setKeywordFilter('')
  }
  const resetStatusSet = (): void => {
    props.fullProps?.setStatusesFilter(new Set())
  }
  const resetDeactivatingIn = (): void => {
    props.fullProps?.setDeactivatingInFilter?.('')
  }
  const resetCardholders = (): void => {
    props.fullProps?.setCardholdersFilter?.(new Set())
  }
  const resetCreditCards = (): void => {
    props.fullProps?.setCreditCardsFilter?.(new Set())
  }

  const onFiltersResetClick = (): void => {
    resetKeywordFilter()
    resetStatusSet()
    resetDeactivatingIn()
    resetCardholders()
    pushUrlStateDebounce()
    resetCreditCards()
  }

  const hasUserAppliedFilters =
    props.fullProps?.keywordFilter ||
    (props.fullProps?.statusesFilter &&
      props.fullProps?.statusesFilter.length > 0) ||
    props.fullProps?.deactivatingInFilter ||
    (props.fullProps?.cardholdersFilter &&
      props.fullProps?.cardholdersFilter.length > 0) ||
    (props.fullProps?.creditCardsFilter &&
      props.fullProps?.creditCardsFilter.length > 0)

  const filtersAppliedCount = (): number => {
    let appliedCount = 0
    if (
      props.fullProps?.statusesFilter &&
      props.fullProps?.statusesFilter.length > 0
    ) {
      appliedCount++
    }
    if (props.fullProps?.deactivatingInFilter) {
      appliedCount++
    }
    if (
      props.fullProps?.cardholdersFilter &&
      props.fullProps?.cardholdersFilter.length > 0
    ) {
      appliedCount++
    }
    if (
      props.fullProps?.creditCardsFilter &&
      props.fullProps?.creditCardsFilter.length > 0
    ) {
      appliedCount++
    }
    return appliedCount
  }

  const emptyStateDesign = hasUserAppliedFilters
    ? TableEmptyStateDesigns.BillPayFiltered
    : TableEmptyStateDesigns.BillPay

  return (
    <NavAwayBox
      navAway={
        props.totalCount > 0 && props.snapshotProps !== undefined
          ? props.snapshotProps.navAway
          : undefined
      }
      loading={props.loading}
    >
      <FilterSection
        hideButtonChevron
        appliedFiltersCount={filtersAppliedCount()}
        filtersFlyoutProps={
          props.fullProps !== undefined && !props.hideFilters
            ? {
                title: translate('Bill Pay Filters'),
                onReset: onFiltersResetClick,
                loading: props.loading,
                filters: (
                  <>
                    <div
                      css={css`
                        display: flex;
                        flex-direction: column;
                        gap: 12px;
                      `}
                    >
                      <div
                        css={css`
                          ${getFontCss(
                            14,
                            themeConstants.colors.font_primary,
                            styleConstants.fontFamilies.medium,
                          )};
                        `}
                      >
                        <Trns source={'Card Status'} />
                      </div>
                      <Checkbox
                        label={<Trns source={'Active'} />}
                        onClick={(): void => {
                          props.fullProps?.handleStatusSelection(
                            VirtualCardFilterStatus.active,
                          )
                          pushUrlStateDebounce()
                        }}
                        isSelected={
                          props.fullProps?.statusesFilter.includes(
                            VirtualCardFilterStatus.active,
                          ) || false
                        }
                      />
                      <Checkbox
                        label={<Trns source={'Pending'} />}
                        onClick={(): void => {
                          props.fullProps?.handleStatusSelection(
                            VirtualCardFilterStatus.pending,
                          )
                          pushUrlStateDebounce()
                        }}
                        isSelected={
                          props.fullProps?.statusesFilter.includes(
                            VirtualCardFilterStatus.pending,
                          ) || false
                        }
                      />
                      <Checkbox
                        label={<Trns source={'Inactive'} />}
                        onClick={(): void => {
                          props.fullProps?.handleStatusSelection(
                            VirtualCardFilterStatus.inactive,
                          )
                          pushUrlStateDebounce()
                        }}
                        isSelected={
                          props.fullProps?.statusesFilter.includes(
                            VirtualCardFilterStatus.inactive,
                          ) || false
                        }
                      />
                      <Checkbox
                        label={<Trns source={'Closed'} />}
                        onClick={(): void => {
                          props.fullProps?.handleStatusSelection(
                            VirtualCardFilterStatus.closed,
                          )
                          pushUrlStateDebounce()
                        }}
                        isSelected={
                          props.fullProps?.statusesFilter.includes(
                            VirtualCardFilterStatus.closed,
                          ) || false
                        }
                      />
                    </div>
                    <DividerLineDiv
                      css={css`
                        margin: 20px 0;
                      `}
                    />
                    <div
                      css={css`
                        display: flex;
                        flex-direction: column;
                        gap: 12px;
                      `}
                    >
                      <div
                        css={css`
                          ${getFontCss(
                            14,
                            themeConstants.colors.font_primary,
                            styleConstants.fontFamilies.medium,
                          )};
                        `}
                      >
                        <Trns source={'Cards Deactivating Within'} />
                      </div>
                      <div
                        css={css`
                          display: flex;
                          align-items: center;
                          gap: 8px;
                        `}
                      >
                        <RadioButton
                          onClick={(): void => {
                            props.fullProps?.setDeactivatingInFilter?.(
                              VirtualCardDeactivatingInIncrements.in7Days,
                            )
                            pushUrlStateDebounce()
                          }}
                          isSelected={
                            props.fullProps?.deactivatingInFilter ===
                            VirtualCardDeactivatingInIncrements.in7Days
                          }
                          css={css`
                            display: block;
                          `}
                        />
                        <div
                          css={css`
                            ${getFontCss(
                              14,
                              themeConstants.colors.font_primary,
                              styleConstants.fontFamilies.regular,
                            )}
                          `}
                        >
                          <Trns source={'7 days'} />
                        </div>
                      </div>
                      <div
                        css={css`
                          display: flex;
                          align-items: center;
                          gap: 8px;
                        `}
                      >
                        <RadioButton
                          onClick={(): void => {
                            props.fullProps?.setDeactivatingInFilter?.(
                              VirtualCardDeactivatingInIncrements.in14Days,
                            )
                            pushUrlStateDebounce()
                          }}
                          isSelected={
                            props.fullProps?.deactivatingInFilter ===
                            VirtualCardDeactivatingInIncrements.in14Days
                          }
                          css={css`
                            display: block;
                          `}
                        />
                        <div
                          css={css`
                            ${getFontCss(
                              14,
                              themeConstants.colors.font_primary,
                              styleConstants.fontFamilies.regular,
                            )}
                          `}
                        >
                          <Trns source={'14 days'} />
                        </div>
                      </div>
                      <div
                        css={css`
                          display: flex;
                          align-items: center;
                          gap: 8px;
                        `}
                      >
                        <RadioButton
                          onClick={(): void => {
                            props.fullProps?.setDeactivatingInFilter?.(
                              VirtualCardDeactivatingInIncrements.in30Days,
                            )
                            pushUrlStateDebounce()
                          }}
                          isSelected={
                            props.fullProps?.deactivatingInFilter ===
                            VirtualCardDeactivatingInIncrements.in30Days
                          }
                          css={css`
                            display: block;
                          `}
                        />
                        <div
                          css={css`
                            ${getFontCss(
                              14,
                              themeConstants.colors.font_primary,
                              styleConstants.fontFamilies.regular,
                            )}
                          `}
                        >
                          <Trns source={'30 days'} />
                        </div>
                      </div>
                      <div
                        css={css`
                          display: flex;
                          align-items: center;
                          gap: 8px;
                        `}
                      >
                        <RadioButton
                          onClick={(): void => {
                            resetDeactivatingIn()
                            pushUrlStateDebounce()
                          }}
                          isSelected={
                            props.fullProps?.deactivatingInFilter === ''
                          }
                          css={css`
                            display: block;
                          `}
                        />
                        <div
                          css={css`
                            ${getFontCss(
                              14,
                              themeConstants.colors.font_primary,
                              styleConstants.fontFamilies.regular,
                            )}
                          `}
                        >
                          <Trns source={'Any time'} />
                        </div>
                      </div>
                    </div>
                    <DividerLineDiv
                      css={css`
                        margin: 20px 0px;
                      `}
                    />
                    <div
                      css={css`
                        ${getFontCss(
                          14,
                          themeConstants.colors.font_primary,
                          styleConstants.fontFamilies.medium,
                        )};
                      `}
                    >
                      <Trns source={'Card Creator'} />
                    </div>
                    <UserDropdown
                      css={css`
                        margin-top: 8px;
                      `}
                      isCreatable={false}
                      placeholder={translateSingleString(
                        locale,
                        'All bill pay card creators',
                      )}
                      chooseOption={(val: string): void => {
                        props.fullProps?.handleCardholderSelection?.(val)
                        pushUrlStateDebounce()
                      }}
                      isMulti
                      selectedValues={
                        new Set(props.fullProps?.cardholdersFilter)
                      }
                      optionGroups={[{ options: cardCreatorOptions }]}
                      onResetClick={(): void => {
                        resetCardholders()
                        pushUrlStateDebounce()
                      }}
                      infiniteScrollProps={cardCreatorInfiniteScrollProps}
                      minimalDisplay
                    />
                    <DividerLineDiv
                      css={css`
                        margin: 20px 0;
                      `}
                    />
                    <div
                      css={css`
                        ${getFontCss(
                          14,
                          themeConstants.colors.font_primary,
                          styleConstants.fontFamilies.medium,
                        )};
                      `}
                    >
                      <Trns source={'Accounts'} />
                    </div>
                    <CreditCardDropdown
                      css={css`
                        margin-top: 8px;
                      `}
                      placeholder={translateSingleString(
                        locale,
                        'All accounts',
                      )}
                      chooseOption={(val: string): void => {
                        props.fullProps?.handleCreditCardSelection?.(val)
                        pushUrlStateDebounce()
                      }}
                      isMulti
                      selectedValues={
                        new Set(props.fullProps?.creditCardsFilter)
                      }
                      optionGroups={[{ options: creditCardOptions }]}
                      onResetClick={(): void => {
                        resetCreditCards()
                        pushUrlStateDebounce()
                      }}
                      infiniteScrollProps={creditCardInfiniteScrollProps}
                    />
                  </>
                ),
              }
            : undefined
        }
        topLeftContent={
          <div
            css={css`
              display: flex;
              width: 100%;
              align-items: center;
              justify-content: space-between;
            `}
          >
            {hasUserAppliedFilters && props.fullProps ? (
              props.loading && !props.complete ? (
                <Processing />
              ) : (
                <span
                  css={css`
                    ${getFontCss(
                      20,
                      themeConstants.colors.highlight,
                      styleConstants.fontFamilies.regular,
                    )};
                  `}
                >
                  {props.totalCount === 1 ? (
                    props.fullProps.filteredCount &&
                    props.fullProps.filteredCount === 1 ? (
                      <Trns
                        source={
                          '${filteredCount} Result (of ${totalCount} card)'
                        }
                        expressions={{
                          filteredCount: `${props.fullProps.filteredCount || 0}`,
                          totalCount: `${props.totalCount}`,
                        }}
                      />
                    ) : (
                      <Trns
                        source={
                          '${filteredCount} Results (of ${totalCount} card)'
                        }
                        expressions={{
                          filteredCount: `${props.fullProps.filteredCount || 0}`,
                          totalCount: `${props.totalCount}`,
                        }}
                      />
                    )
                  ) : props.fullProps.filteredCount &&
                    props.fullProps.filteredCount === 1 ? (
                    <Trns
                      source={
                        '${filteredCount} Result (of ${totalCount} cards)'
                      }
                      expressions={{
                        filteredCount: `${props.fullProps.filteredCount || 0}`,
                        totalCount: `${props.totalCount}`,
                      }}
                    />
                  ) : (
                    <Trns
                      source={
                        '${filteredCount} Results (of ${totalCount} cards)'
                      }
                      expressions={{
                        filteredCount: `${props.fullProps.filteredCount || 0}`,
                        totalCount: `${props.totalCount}`,
                      }}
                    />
                  )}
                </span>
              )
            ) : (
              props.defaultTitle
            )}
            {props.fullProps && (
              <div
                css={css`
                  flex-grow: 1;
                  padding-left: 40px;
                `}
              >
                <KeywordFilter
                  value={props.fullProps.keywordFilter}
                  onChange={(val: string): void => {
                    // call init action to set loading state immediately
                    dispatch(createGetVirtualCardsInitAction())
                    onKeywordChange(val)
                    pushUrlStateDebounce()
                  }}
                  placeholder={translateSingleString(
                    locale,
                    'Vendor, email, invoice number, etc.',
                  )}
                />
              </div>
            )}
          </div>
        }
      />
      <div
        css={css`
          ${!props.isSnapshot &&
          css`
            border-bottom-left-radius: 10px;
            border-bottom-right-radius: 10px;
          `}
        `}
      >
        <BillPayTable
          virtualCards={props.virtualCards}
          virtualCardsPermissions={props.virtualCardsPermissions}
          sortableProps={props.fullProps?.sortableProps}
          checkboxProps={props.checkboxProps}
          infiniteScrollProps={props.infiniteScrollProps}
          filtersBody={props.filtersBody}
          clientHeight={props.clientHeight}
          emptyStateDesign={emptyStateDesign}
        />
      </div>
    </NavAwayBox>
  )
}

export default BillPayBox
